import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./FaqSpotlight.scss";
import { FormattedMessage } from "react-intl";
import MoreDetails from "app/pages/.shared/MoreDetails";
import RelativeLink from "app/pages/.shared/RelativeLink";
import { sendTagOnFaqSpotlightArticleClick } from "app/utils/analytics";

const FaqSpotlight = ({ title, message, articles }) => {
	const [moreSpotlightText, showMoreSpotlightText] = useState(false);
	const [spotlightTextLineClamped, toggleSpotlightTextLineClamped] = useState(false);

	const messageEl = useRef(null);

	const toggleMoreSpotlightText = useCallback(() => {
		showMoreSpotlightText(!moreSpotlightText);
	}, [moreSpotlightText]);

	const spotlightTextClass = classNames({
		"faq-spotlight__message": true,
		"faq-spotlight__message--expanded": moreSpotlightText,
	});

	useEffect(() => {
		// detect if spotlight message is too long and being cropped
		// @see https://stackoverflow.com/questions/52232026/determine-if-ellipsis-is-being-displayed-when-using-webkit-line-clamp-for-multi
		if (messageEl.current && messageEl.current.offsetHeight < messageEl.current.scrollHeight) {
			toggleSpotlightTextLineClamped(true);
		}
	}, [messageEl.current]);

	return (
		<div className="faq-spotlight">
			<div className="faq-spotlight__headline">
				<i className="icon icon--warning-white" />
				<div className="faq-spotlight__title">{title}</div>
			</div>

			<div className="faq-spotlight__content">
				<div className={spotlightTextClass} ref={messageEl}>
					{message.map((mes, idx) => (
						<React.Fragment key={idx}>
							<div
								className="faq-spotlight__line"
								dangerouslySetInnerHTML={{ __html: mes }}
							/>
							<br />
						</React.Fragment>
					))}
				</div>
				{spotlightTextLineClamped && (
					<div className="faq-spotlight__more-button" onClick={toggleMoreSpotlightText}>
						{moreSpotlightText ? (
							<FormattedMessage id="general.see.less" />
						) : (
							<FormattedMessage id="general.see.more" />
						)}
					</div>
				)}

				{articles.length > 0 && (
					<div className="faq-spotlight__articles">
						<div className="faq-spotlight__article-list">
							{articles.slice(0, 4).map(article => {
								const handleArticleClick = () => {
									sendTagOnFaqSpotlightArticleClick(article.title);
								};

								return (
									<div className="faq-spotlight__article" key={article.objectID}>
										<RelativeLink
											to={{
												pathname: `/faq/category/${
													article.category.id
												}/subcategory/${article.subCategory.id}/article/${
													article.id
												}`,
											}}
											onClick={handleArticleClick}
										>
											{article.title}
										</RelativeLink>
									</div>
								);
							})}
						</div>
						{articles.length > 4 && (
							<MoreDetails
								headerPosition="bottom"
								expandedLabel={<FormattedMessage id="general.see.less" />}
								collapsedLabel={<FormattedMessage id="general.see.more" />}
							>
								<div className="faq-spotlight__article-list">
									{articles.slice(4).map(article => {
										const handleArticleClick = () => {
											sendTagOnFaqSpotlightArticleClick(article.title);
										};

										return (
											<div
												className="faq-spotlight__article"
												key={article.objectID}
											>
												<RelativeLink
													to={{
														pathname: `/faq/category/${
															article.category.id
														}/subcategory/${
															article.subCategory.id
														}/article/${article.id}`,
													}}
													onClick={handleArticleClick}
												>
													{article.title}
												</RelativeLink>
											</div>
										);
									})}
								</div>
							</MoreDetails>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

FaqSpotlight.propTypes = {
	title: PropTypes.string,
	message: PropTypes.array,
	articles: PropTypes.array,
};

FaqSpotlight.defaultProps = {
	message: [],
	articles: [],
};

export default FaqSpotlight;
